import _, { method } from 'lodash'
import React from 'react'
import { useTheme } from 'styled-components'

import authorization from 'live/services/authorization'
import i18n from 'live/services/i18n'

import { IBarrierTypeIcon, IMethod } from 'plugin-codaf-risk-ext-bowtie/types'
import { Icon } from 'plugin-codaf-risk-ext-bowtie/utils'
import {
    barrierEditorGap,
    barrierHeight,
    barrierWidth,
    escalationHeight,
    escalationWidth,
    unfocusOpacity
} from 'plugin-codaf-risk-ext-bowtie/utils/constants'

import { IBarrierTypes, IEditorBarrier, IEditorEscalation } from '../../../../../../BarrierEditor'
import BarrierToEdit from '../../BarrierToEdit'
import AddButton from '../AddButton/AddButton'
import { EscalationContainer, TextContainer } from './styles'
import { HoldFunction } from 'plugin-codaf-risk-ext-bowtie/types/enums'

type IProps = {
    escalationData: IEditorEscalation
    yFather: number
    x: number
    y: number
    handleEditBarrier?: (k: IEditorBarrier | IEditorEscalation, type: IBarrierTypes) => void
    handleDeleteEscalationClick?: () => void
    handleBarrierChange: () => void
    editingItem: IEditorBarrier | IEditorEscalation
    handleGetRecursiveSearch: (barrierRef: IEditorBarrier) => IEditorBarrier[]
    handleGetRecursiveSearchFromEscalation: (escalationRef: IEditorEscalation) => IEditorEscalation[]
    selectedMethod: IMethod
    barrierTypeIconMap: Map<number, IBarrierTypeIcon>
}

export const EscalationFactor: React.FC<IProps> = ({
    escalationData,
    yFather,
    x,
    y,
    handleEditBarrier,
    handleDeleteEscalationClick: handleDeleteClick,
    handleBarrierChange,
    editingItem,
    handleGetRecursiveSearch,
    handleGetRecursiveSearchFromEscalation,
    selectedMethod,
    barrierTypeIconMap
}) => {
    const stateX = x - barrierWidth * 2 - (escalationData.barriers ?? []).length * (barrierWidth + barrierEditorGap)
    const bowtieAdmin = authorization.superAdmin || authorization.Permission.BOWTIE_ADMIN

    const theme = useTheme()

    const handleAddBarrier = (): void => {
        const recursiveArrayOfBarrierOfEscalation = handleGetRecursiveSearchFromEscalation(escalationData)

        const barrierToAdd: IEditorBarrier = {
            id: undefined,
            uid: _.uniqueId(),
            name: null,
            barrierType: null,
            root: false,
            supervisor: null,
            criticality: null,
            method: selectedMethod,
            updateModeCode: null,
            updateModeJsonConfig: null,
            escalations: [],
            getFather: escalationData,
            manualInteraction: null
        }

        if (recursiveArrayOfBarrierOfEscalation[0].id) {
            recursiveArrayOfBarrierOfEscalation.forEach(esc => {
                if (esc.barriers.length) {
                    esc.barriers = [
                        ...esc.barriers.filter((b: IEditorBarrier) => b.uid !== barrierToAdd.uid),
                        barrierToAdd
                    ]
                } else {
                    esc.barriers = [barrierToAdd]
                }
            })
        } else {
            if (recursiveArrayOfBarrierOfEscalation[0].barriers.length) {
                recursiveArrayOfBarrierOfEscalation[0].barriers = [
                    ...recursiveArrayOfBarrierOfEscalation[0].barriers,
                    barrierToAdd
                ]
            } else {
                recursiveArrayOfBarrierOfEscalation[0].barriers = [barrierToAdd]
            }
        }

        handleBarrierChange()
        handleEditBarrier(barrierToAdd, 'ESCALATIONBARRIER')
    }

    const handleBeforeExpanded = (esc: IEditorEscalation): number => {
        let number = 1

        if (esc.barriers) {
            for (const barrier of esc.barriers) {
                if (barrier.escalations) {
                    for (const escalation of barrier.escalations as IEditorEscalation[]) {
                        number += handleBeforeExpanded(escalation)
                    }
                }
            }
        }

        return number
    }

    const onDeleteBarrierRequest = (item: IEditorBarrier): void => {
        const recursiveArrayOfBarrierOfEscalation = handleGetRecursiveSearchFromEscalation(escalationData)

        recursiveArrayOfBarrierOfEscalation.forEach(esc => {
            const newArray = (esc.barriers as IEditorBarrier[]).filter(e => {
                if (item.id) {
                    return e.id !== item.id
                } else {
                    return e.uid !== item.uid
                }
            })

            esc.barriers = [...newArray]
        })
        handleBarrierChange()
    }

    return (
        <>
            <path
                d={`M ${x + barrierWidth / 2} ${yFather + barrierHeight} V ${y + escalationHeight / 2}  H ${stateX +
                    escalationWidth}`}
                fill="none"
                strokeWidth={4}
                stroke={theme['gray-400']}
                opacity={editingItem ? (editingItem === escalationData ? 1 : unfocusOpacity) : 1}
            />
            <EscalationContainer
                transform={`translate(${stateX} ${y})`}
                style={{ cursor: 'pointer' }}
                opacity={editingItem ? (editingItem === escalationData ? 1 : unfocusOpacity) : 1}
            >
                {bowtieAdmin && (
                    <g className={`delete-escalation-button`}>
                        <rect width={escalationWidth + 18} height={escalationHeight / 2 - 2} fill={theme['gray-200']} />

                        <Icon
                            type="delete"
                            scale="1.2"
                            color={theme['gray-200']}
                            auxliarColor={theme['red-500']}
                            x={escalationWidth}
                            clickEvent={handleDeleteClick}
                        />
                    </g>
                )}
                <path
                    d="M124.307 1H5.06542C2.82015 1 1 2.82015 1 5.06542V64.9346C1 67.1798 2.82015 69 5.06542 69H124.249C125.911 69 127.405 67.9887 128.023 66.4462L140.377 35.6018C140.776 34.6057 140.765 33.4923 140.347 32.5043L128.05 3.47957C127.414 1.97645 125.94 1 124.307 1Z"
                    fill="#F9E47F"
                    stroke={editingItem === escalationData ? '#37A3EF' : '#3866B1'}
                    strokeWidth={editingItem === escalationData ? 4 : '0.813084'}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
                <foreignObject
                    x={8}
                    y={8}
                    width={escalationWidth - 25}
                    height={escalationHeight - 16}
                    style={{ overflow: 'hidden' }}
                >
                    <TextContainer>{escalationData?.name}</TextContainer>
                </foreignObject>

                <path
                    d="M124.307 1H5.06542C2.82015 1 1 2.82015 1 5.06542V64.9346C1 67.1798 2.82015 69 5.06542 69H124.249C125.911 69 127.405 67.9887 128.023 66.4462L140.377 35.6018C140.776 34.6057 140.765 33.4923 140.347 32.5043L128.05 3.47957C127.414 1.97645 125.94 1 124.307 1Z"
                    fill="#F9E47F"
                    opacity={0}
                    onClick={() => handleEditBarrier(escalationData, 'ESCALATIONFACTOR')}
                />
            </EscalationContainer>

            {!(selectedMethod.method === 'QUALITATIVE' && escalationData.barriers.length === 1) && (
                <AddButton
                    escalationLength={escalationData.barriers.length}
                    x={x - (escalationData.barriers.length > 0 ? 0 : 56)}
                    y={y + escalationHeight / 2 - 20}
                    onAddRequest={handleAddBarrier}
                    customText={i18n('Barrier')}
                    type="ESCALATIONBARRIER"
                    opacity={editingItem ? unfocusOpacity : 1}
                />
            )}

            {escalationData.barriers?.map((barrier: IEditorBarrier, index, arr: IEditorBarrier[]) => {
                const barrierX = stateX + escalationWidth + (barrierWidth + barrierEditorGap) * index + 30

                const barrierY = y + escalationHeight / 2 + 8

                const beforeBrothersEscalationsLength = []

                for (let i = 0; i < index; i++) {
                    if (arr[i].escalations) {
                        for (const escalation of arr[i].escalations as IEditorEscalation[]) {
                            beforeBrothersEscalationsLength.push(handleBeforeExpanded(escalation))
                        }
                    }
                }

                return (
                    <BarrierToEdit
                        key={index}
                        barrierData={barrier as IEditorBarrier}
                        x={barrierX}
                        y={barrierY}
                        zoomScale={1}
                        primaryBarrier={false}
                        handleEditBarrier={(k, type) => handleEditBarrier(k, type)}
                        beforeBrothersEscalationsLength={beforeBrothersEscalationsLength.reduce((a, b) => a + b, 0)}
                        handleDeleteBarrierClick={() => onDeleteBarrierRequest(barrier as IEditorBarrier)}
                        handleBarrierChange={handleBarrierChange}
                        editingItem={editingItem}
                        handleGetRecursiveSearch={handleGetRecursiveSearch}
                        handleGetRecursiveSearchFromEscalation={handleGetRecursiveSearchFromEscalation}
                        selectedMethod={selectedMethod}
                        barrierTypeIconMap={barrierTypeIconMap}
                    />
                )
            })}
        </>
    )
}
